/* Flooring Surgeons – home page helpers (no dependencies). Everything works without JS; this only enhances. */
(() => {
  const define = (name, cls) => customElements.get(name) || customElements.define(name, cls);

  /* Prev/next buttons for a horizontal snap rail (desktop). */
  define('fs-rail', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.fs-hrail');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      if (!this.track || !this.prev || !this.next) return;
      if (this.hasAttribute('data-loop') && this.track.children.length > 1) return this.loop();
      const step = (dir) => this.track.scrollBy({ left: dir * this.track.clientWidth * 0.9, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      this.prev.addEventListener('click', () => step(-1));
      this.next.addEventListener('click', () => step(1));
      this.update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        this.prev.disabled = this.track.scrollLeft <= 2;
        this.next.disabled = this.track.scrollLeft >= max;
        this.track.classList.toggle('is-end', this.next.disabled); /* reels: drop the right-edge fade at the end */
      };
      this.track.addEventListener('scroll', this.update, { passive: true });
      /* Speed (12 Oct 2026): the first measure waits for the browser's own layout (ResizeObserver) instead of forcing one
         at start-up – also right for rails inside on-demand (content-visibility) sections, which are measured once shown. */
      if ('ResizeObserver' in window) new ResizeObserver(() => this.update()).observe(this.track);
      else { window.addEventListener('resize', this.update, { passive: true }); this.update(); }
    }

    /* Endless rail (home/contact reels – like WP's looping Swiper): two sets of look-alike copies before and after the
       real cards; when the view drifts into a copy zone it is moved by exactly one set (same picture, no jump).
       Copies are aria-hidden, untabbable, have no <video> (posters only – nothing extra downloads) and a click on one
       opens the real reel's pop-up. Arrows step one card and never disable. */
    loop() {
      const t = this.track;
      const real = [...t.children];
      const n = real.length;
      const SETS = 2;
      const copy = (li, i) => {
        const c = li.cloneNode(true);
        [...c.attributes].forEach((a) => a.name.startsWith('data-shopify') && c.removeAttribute(a.name));
        c.dataset.clone = i;
        c.setAttribute('aria-hidden', 'true');
        c.querySelectorAll('video, template').forEach((e) => e.remove());
        c.querySelectorAll('[data-src]').forEach((e) => e.removeAttribute('data-src'));
        c.querySelectorAll('a, button, [tabindex]').forEach((e) => e.setAttribute('tabindex', '-1'));
        return c;
      };
      for (let s = 0; s < SETS; s++) {
        t.prepend(...real.map(copy));
        t.append(...real.map(copy));
      }
      t.classList.add('is-loop');
      const reduce = matchMedia('(prefers-reduced-motion: reduce)');
      const pad = () => parseFloat(getComputedStyle(t).scrollPaddingLeft) || 0;
      const start = () => real[0].offsetLeft - pad();
      const setW = () => t.children[SETS * n + n].offsetLeft - real[0].offsetLeft;
      const card = () => real[1].offsetLeft - real[0].offsetLeft;
      const jump = (d) => {
        t.style.scrollBehavior = 'auto';
        t.scrollLeft += d;
        t.style.scrollBehavior = '';
      };
      let target = null;
      const normalize = () => {
        if (target !== null) return;
        const s = start();
        const w = setW();
        if (!(w > 0) || !t.clientWidth) return;
        const k = Math.round((t.scrollLeft - s) / w); /* whole sets away from the real ones (no loop: never hangs) */
        if (k) jump(-k * w);
      };
      let timer;
      /* done = the view reached the arrow's target (an interrupted smooth scroll also fires scrollend) or went quiet */
      let touching = false; /* never move the rail under a finger */
      t.addEventListener('touchstart', () => { touching = true; target = null; }, { passive: true });
      t.addEventListener('wheel', () => { target = null; }, { passive: true }); /* the user took over from the arrows */
      const lift = () => { touching = false; if (!('onscrollend' in window)) settle(); };
      t.addEventListener('touchend', lift, { passive: true });
      t.addEventListener('touchcancel', lift, { passive: true });
      const done = (force) => {
        if (touching) return;
        if (target !== null && Math.abs(t.scrollLeft - target) > 2) {
          if (force) target = null; /* stalled / interrupted: forget it, but never jump mid-animation */
          return;
        }
        target = null;
        normalize();
      };
      const settle = () => {
        clearTimeout(timer);
        timer = setTimeout(() => done(true), 400);
      };
      /* Browsers with scrollend re-centre right when a scroll finishes (nothing can be interrupted); others after 400ms
         of quiet. */
      if ('onscrollend' in window) t.addEventListener('scrollend', () => done(false));
      else t.addEventListener('scroll', settle, { passive: true });
      window.addEventListener('resize', settle, { passive: true });
      const step = (dir) => {
        const s = start();
        const w = setW();
        const c = card();
        if (!(c > 0) || !(w > 0)) return;
        const base = target ?? t.scrollLeft;
        let to = s + Math.round((base - s) / c) * c + dir * c;
        if (to < s - w) { jump(w); to += w; }
        if (to > s + w) { jump(-w); to -= w; }
        target = to;
        t.scrollTo({ left: to, behavior: reduce.matches ? 'auto' : 'smooth' });
        if (!('onscrollend' in window)) settle();
      };
      this.prev.addEventListener('click', () => step(-1));
      this.next.addEventListener('click', () => step(1));
      /* a copy's play button opens the real reel (same pop-up entry, focus returns to the real card) */
      t.addEventListener('click', (e) => {
        const c = e.target.closest('[data-clone]');
        const b = c && e.target.closest('.fs-reel__play');
        if (!b) return;
        e.preventDefault();
        real[+c.dataset.clone].querySelector('.fs-reel__play')?.click();
      });
      /* first position: after the browser's own layout (no forced reflow at start-up); the copies look the same, so
         nothing visible changes until then */
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(() => { if (!t.clientWidth) return; ro.disconnect(); jump(start() - t.scrollLeft); });
        ro.observe(t);
      } else jump(start() - t.scrollLeft);
    }
  });

  /* Mini m² → packs calculator. Pack size and pack price come from data attributes (product metafields). */
  define('fs-mini-calc', class extends HTMLElement {
    connectedCallback() {
      this.pack = parseFloat(this.dataset.pack) || 0;
      this.price = parseInt(this.dataset.price, 10) || 0;
      this.waste = parseFloat(this.dataset.waste) || 0;
      this.money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: this.dataset.currency || 'GBP' });
      this.inputs = [...this.querySelectorAll('input')];
      this.out = (k) => this.querySelector(`[data-out="${k}"]`);
      this.inputs.forEach((i) => i.addEventListener('input', () => this.calc()));
      this.calc();
    }
    calc() {
      if (!this.pack) return;
      const [l, w] = this.inputs.map((i) => parseFloat(String(i.value).replace(',', '.')) || 0);
      const area = l * w;
      const need = area * (1 + this.waste / 100);
      const packs = area > 0 ? Math.ceil(need / this.pack - 1e-9) : 0;
      const fmt = (n) => n.toLocaleString('en-GB', { maximumFractionDigits: 2 });
      this.out('area').textContent = `${fmt(area)} m²`;
      this.out('need').textContent = `${fmt(need)} m²`;
      this.out('packs').textContent = `${packs} ${packs === 1 ? 'pack' : 'packs'} (${fmt(packs * this.pack)} m²)`;
      if (this.out('total')) this.out('total').textContent = this.money.format((packs * this.price) / 100);
    }
  });

  /* Inspiration reel (like the old site). Desktop hover = a MUTED looped preview in the card (hover is not a user
     gesture, so browsers block sound there); leaving pauses it. Click / tap / Enter = the reel WITH sound: in the
     section's lightbox (<dialog class="fs-rlb">, home/contact) or, where a section has none (About), toggled in place.
     The mp4 URL is only attached on the first hover / click, so the page loads nothing but the posters.
     Reduced motion: no hover preview. Only one video plays at a time. */
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = matchMedia('(hover: hover) and (pointer: fine)');
  let current = null;

  const lightbox = (dlg) => {
    if (dlg._fs) return dlg._fs;
    const $ = (s) => dlg.querySelector(s);
    const v = $('.fs-rlb__video');
    const mute = $('.fs-rlb__mute');
    const who = $('.fs-rlb__who');
    const ig = $('.fs-rlb__ig');
    let opener = null;
    const sync = () => {
      mute.setAttribute('aria-pressed', v.muted);
      mute.setAttribute('aria-label', v.muted ? 'Unmute' : 'Mute');
      dlg.classList.toggle('is-muted', v.muted);
    };
    mute.addEventListener('click', () => { v.muted = !v.muted; if (v.paused) v.play().catch(() => {}); sync(); });
    v.addEventListener('click', () => (v.paused ? v.play().catch(() => {}) : v.pause()));
    v.addEventListener('volumechange', sync);
    $('.fs-rlb__close').addEventListener('click', () => dlg.close());
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); }); /* backdrop */
    /* keep Tab inside the pop-up (a modal <dialog> alone lets focus wander out to the browser UI and back) */
    dlg.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const f = [...dlg.querySelectorAll('a[href], button, [tabindex="0"]')].filter((el) => !el.hidden && el.offsetParent);
      const i = f.indexOf(document.activeElement);
      if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
    });
    dlg.addEventListener('close', () => {
      v.pause();
      v.removeAttribute('src');
      v.load();
      document.documentElement.classList.remove('fs-rlb-open');
      setTimeout(() => opener?.focus({ preventScroll: true })); /* after the browser's own focus restore */
    });
    return (dlg._fs = {
      open(reel) {
        opener = reel.btn;
        const handle = reel.dataset.handle || '';
        const url = reel.dataset.ig || '';
        $('.fs-rlb__avatar').textContent = handle.replace(/^@/, '').slice(0, 2).toUpperCase();
        $('.fs-rlb__user').textContent = handle || 'Customer video';
        $('.fs-rlb__cap').textContent = reel.querySelector('template.fs-reel__cap')?.content.textContent.trim() || '';
        dlg.classList.toggle('has-cap', !!$('.fs-rlb__cap').textContent);
        [who, ig].forEach((a) => (url ? a.setAttribute('href', url) : a.removeAttribute('href')));
        ig.hidden = !url;
        v.poster = reel.dataset.poster || '';
        v.src = reel.dataset.src;
        v.muted = false; /* the click that opened us is the user gesture that allows sound */
        document.documentElement.classList.add('fs-rlb-open');
        dlg.showModal();
        $('.fs-rlb__close').focus({ preventScroll: true });
        $('.fs-rlb__body').scrollTop = 0;
        const p = v.play();
        p?.catch(() => { v.muted = true; v.play().catch(() => {}); }); /* very strict browsers: fall back to muted */
        sync();
      },
    });
  };

  define('fs-reel', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.btn = this.querySelector('.fs-reel__play');
      if (!this.video || !this.btn || !this.dataset.src) return;
      this.lb = this.closest('.fs-reels')?.querySelector('dialog.fs-rlb');
      this.video.muted = true;
      this.video.addEventListener('playing', () => this.set(true));
      this.video.addEventListener('pause', () => this.set(false));
      this.btn.addEventListener('click', () => {
        if (this.lb && typeof this.lb.showModal === 'function') {
          if (current) current.pause(true);
          lightbox(this.lb).open(this);
        } else if (this.video.paused) this.play();
        else this.pause();
      });
      this.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse' && canHover.matches && !reduce.matches) this.play();
      });
      this.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') this.pause(true);
      });
      /* Pause a reel that is swiped / scrolled out of view. */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => en.isIntersecting || this.pause(), { threshold: 0.25 }).observe(this);
      }
    }
    play() {
      const v = this.video;
      if (this.lb?.open) return; /* the lightbox owns the sound – no previews behind it */
      if (!v.getAttribute('src')) {
        v.preload = 'auto';
        v.src = this.dataset.src;
      }
      if (current && current !== this) current.pause(true);
      current = this;
      this.classList.add('is-loading');
      v.play()?.catch(() => this.classList.remove('is-loading'));
    }
    pause(reset) {
      const v = this.video;
      if (!v) return;
      this.classList.remove('is-loading');
      if (!v.paused) v.pause();
      if (reset && v.readyState > 0) v.currentTime = 0;
      if (reset) this.classList.remove('is-started');
    }
    set(on) {
      this.classList.toggle('is-playing', on);
      if (on) this.classList.add('is-started');
      this.classList.remove('is-loading');
      if (this.lb) return; /* lightbox reels: the button always opens the pop-up */
      this.btn.setAttribute('aria-pressed', on);
      if (this.btn.dataset.play) this.btn.setAttribute('aria-label', this.btn.dataset[on ? 'pause' : 'play']);
    }
  });

  /* Click-to-load Google Map: nothing third-party loads until the visitor asks for it. */
  define('fs-map', class extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('button');
      btn?.addEventListener('click', () => {
        const f = document.createElement('iframe');
        f.src = `https://www.google.com/maps?q=${encodeURIComponent(this.dataset.q)}&output=embed`;
        f.title = this.dataset.title || 'Map';
        f.loading = 'lazy';
        f.referrerPolicy = 'no-referrer-when-downgrade';
        this.append(f);
        this.closest('.fs-show__visual')?.classList.add('is-map');
      }, { once: true });
    }
  });
})();
